import React from 'react';
import { PanelType, Stack } from '@fluentui/react';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { CopilotProvider } from '@fluentui-copilot/react-copilot';
import { Text, useAppTheme } from 'common';
import { IOfficeShellPanelProps } from 'common/components/Layout/OfficeShellPanel/OfficeShellPanel.types';
import { Panel } from 'common/components/Panel';
import { usePanelMobileView } from 'common/components/Panel/usePanelMobileView';
import { pageDataService } from 'common/utilities/PageDataService';
import { useLogViewOnMount } from 'common/utilities/telemetryHelpers';
import { useLocalizedStrings } from 'i18n';
import { useRaiseWebViewRenderedEvent, useWebView } from 'store/webView/useWebView';

import { useChatControlStyles, useChatPanelStyles } from './Chat.styles';
import { ChatContextProvider } from './ChatContextProvider';
import { ChatControl } from './ChatControl';
import { ChatInput } from './ChatInput';

const ChatPanel = (props: IOfficeShellPanelProps<'GlobalCopilot'>) => {
  // i18next-extract-mark-ns-start copilot

  useRaiseWebViewRenderedEvent('ChatPanel', false, false);
  useLogViewOnMount('ChatPanel');

  const { t } = useLocalizedStrings('copilot');
  const { isWebView } = useWebView();
  const classNames = useChatControlStyles();
  const { isMobileView } = usePanelMobileView();
  const panelStyles = useChatPanelStyles();
  const theme = useAppTheme();

  // TODO: remove the debug link below
  // eslint-disable-next-line i18next/no-literal-string
  const copilotPlayground = 'Try Copilot playground';

  const onRenderNavigationContent = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (renderProps: any, defaultRender?: (props?: any) => JSX.Element | null) => (
      <Stack grow horizontal horizontalAlign="space-between" verticalAlign="center">
        <Stack.Item className={classNames.panelPreviewTagHeader}>
          <Text variant="Caption2">{t('ChatPanel.PreviewTag')}</Text>
        </Stack.Item>
        {!isWebView && !pageDataService.isProdEnvironment() && (
          <Stack.Item>
            <a href="/sandbox/copilot">{copilotPlayground}</a>
          </Stack.Item>
        )}
        <Stack.Item>{defaultRender!(renderProps)}</Stack.Item>
      </Stack>
    ),
    [classNames.panelPreviewTagHeader, isWebView, t]
  );

  const onRenderFooterContent = React.useCallback(
    () => (
      <FluentProvider theme={{ ...webLightTheme, colorNeutralBackground1: 'transparent' }}>
        <CopilotProvider mode="sidecar">
          <ChatInput />
        </CopilotProvider>
      </FluentProvider>
    ),
    []
  );

  if (!pageDataService.isCopilotEnabled()) {
    return null;
  }

  return (
    <ChatContextProvider>
      <Panel
        isBlocking
        isLightDismiss={false}
        type={PanelType.custom}
        customWidth="360px"
        closeButtonAriaLabel={t('ChatPanel.CloseButtonAriaLabel')}
        onRenderNavigationContent={isMobileView ? undefined : onRenderNavigationContent}
        onRenderFooterContent={onRenderFooterContent}
        isFooterAtBottom={true}
        headerText={isMobileView ? t('ChatPanel.PreviewTag') : undefined}
        styles={panelStyles}
        bgColorOverride={theme.appColors?.copilotPanelBackground}
        {...props}
      >
        <ChatControl hideChatInput={true} />
      </Panel>
    </ChatContextProvider>
  );

  // i18next-extract-mark-ns-stop copilot
};

export default ChatPanel;
